import React, { useEffect, useState } from 'react';
import { PMNode, Process } from '../../types';
import type { Segment } from '../../migrations/legacyEdge';
import { useSessionStore } from '../../store/sessionStore';
import { processToPseudocode } from '../../utils/export';
import { validateProcessSegments } from '../../utils/processValidation';
import { OPERATOR_BADGE, formatHeaderLabel, getOperatorColor, getSegmentOperator } from './segmentPresentation';
import { buildEdgeMap, resolveProcessEdges } from '../../utils/processEdges';

interface ProcessCardProps {
  proc: Process;
  order: number;
  isActive: boolean;
  onSegmentEdit: (procId: number, segIdx: number) => void;
  onSegmentHover?: (procId: number, segIdx: number, hovered: boolean) => void;
  hoveredSegmentKey?: string | null;
  isDragTarget?: boolean;
}

const FLOW_EMOJI: Record<string, string> = {
  normal: '→',
  branch: '↔️',
  loop: '↺',
  fail: '⚠️',
  parallel: '∥',
  wait: '⏳',
};


const INDENT_PX = 16;

interface DecoratedSegment {
  seg: Segment;
  idx: number;
  headerLabel?: string;
  operatorBadge?: string;
}


function buildDecoratedSegments(segments: Segment[]): DecoratedSegment[] {
  const depthByBlock = new Map<string, number>();

  return segments.map((seg, idx) => {

    const op = getSegmentOperator(seg);
    return {
      seg,
      idx,
      headerLabel: formatHeaderLabel(seg),
      operatorBadge: OPERATOR_BADGE[op] ?? OPERATOR_BADGE.normal,
    };
  });
}

const ProcessCard: React.FC<ProcessCardProps> = ({
  proc,
  order,
  isActive,
  onSegmentEdit,
  onSegmentHover,
  hoveredSegmentKey = null,
  isDragTarget = false,
}) => {
  const toggleVisibility = useSessionStore(s => s.toggleProcessVisibility);
  const setActive       = useSessionStore(s => s.setActiveProcess);
  const deleteProcess    = useSessionStore(s => s.deleteProcess);
  const renameProcess    = useSessionStore(s => s.renameProcess);
  const nodes            = useSessionStore(s => s.nodes);
  const edges            = useSessionStore(s => s.edges);
  const reorderSegment   = useSessionStore(s => s.reorderSegment);
  const deleteSegment    = useSessionStore(s => s.deleteSegment);
  const lastValidationWarning = useSessionStore(s => s.lastValidationWarning);
  const setLastValidationWarning = useSessionStore(s => s.setLastValidationWarning);
  const [draggingSegIdx, setDraggingSegIdx] = useState<number | null>(null);
  const [dragOverSegIdx, setDragOverSegIdx] = useState<number | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(proc.name);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const nodeById = nodes as Record<string, PMNode>;
  const segments = resolveProcessEdges(proc, buildEdgeMap(edges));

  useEffect(() => {
    if (!isEditingName) setDraftName(proc.name);
  }, [proc.name, isEditingName]);

  const totalSegments = segments.length;
  const decoratedSegments = buildDecoratedSegments(segments);
  const processValidation = validateProcessSegments({ segments });
  const hasStructureErrors = !processValidation.isValid;

  const saveName = () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      setDraftName(proc.name);
      setIsEditingName(false);
      return;
    }
    renameProcess(proc.id, trimmed);
    setIsEditingName(false);
  };

  const showFeedback = (message: string) => {
    setCopyFeedback(message);
    window.setTimeout(() => setCopyFeedback(null), 1800);
  };

  const copyProcessPseudocode = async () => {
    const pseudo = processToPseudocode(proc, nodeById, segments);
    try {
      await navigator.clipboard.writeText(pseudo);
      showFeedback('Pseudocódigo copiado');
    } catch (err) {
      alert('No se pudo copiar al portapapeles. Copia manual:\n\n' + pseudo);
      showFeedback('No se pudo copiar');
    }
  };
  
  return (
    <div
      style={{
      borderRadius: 10,
      border: `1.5px solid ${isActive ? proc.color : '#ece9e1'}`,
      background: isActive ? `${proc.color}08` : '#fff',
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      transition: 'all 0.2s ease',
      position: 'relative',
      boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
      outline: isDragTarget ? `2px dashed ${proc.color}` : 'none',
      cursor: 'default',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <span title="Orden del proceso" style={{
            fontSize: 11,
            minWidth: 20,
            height: 20,
            borderRadius: 999,
            border: '1px solid #e8e1d5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8e8679',
            fontFamily: "'DM Mono',monospace",
            background: '#faf8f3',
          }}>
            {order}
          </span>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', background: proc.color, flexShrink: 0,
            boxShadow: `0 0 0 2px ${proc.color}33`,
          }} />
          {hasStructureErrors && (
            <span
              title={processValidation.issues.map(issue => `• ${issue.message}`).join('\n')}
              style={{
                fontSize: 10,
                borderRadius: 999,
                padding: '2px 6px',
                background: '#c0392b1a',
                color: '#c0392b',
                fontFamily: "'DM Mono',monospace",
                border: '1px solid #c0392b44',
                whiteSpace: 'nowrap',
              }}
            >
              ⚠ {processValidation.issues.length}
            </span>
          )}
          {isEditingName ? (
            <input
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveName();
                if (e.key === 'Escape') {
                  setDraftName(proc.name);
                  setIsEditingName(false);
                }
              }}
              onBlur={saveName}
              autoFocus
              style={{
                fontFamily: "'Caveat',cursive",
                fontSize: 18,
                fontWeight: 700,
                color: '#333',
                border: `1px solid ${proc.color}55`,
                borderRadius: 6,
                padding: '2px 6px',
                outline: 'none',
                background: '#fff',
                width: '100%',
                minWidth: 0,
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraftName(proc.name);
                setIsEditingName(true);
              }}
              title="Editar nombre del proceso"
              style={{
                fontFamily: "'Caveat',cursive",
                fontSize: 18,
                fontWeight: 700,
                color: '#333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                cursor: 'text',
                textAlign: 'left',
              }}
            >
              {proc.name}
            </button>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            onClick={() => setIsCollapsed(prev => !prev)}
            title={isCollapsed ? 'Desplegar proceso' : 'Plegar proceso'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              opacity: 0.75,
              padding: 4,
            }}
          >
            {isCollapsed ? '▾' : '▴'}
          </button>
          <button
            type="button"
            onClick={copyProcessPseudocode}
            title="Copiar pseudocódigo del proceso"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              opacity: 0.75,
              padding: 4,
            }}
          >
            {'{}'}
          </button>
          <span title="Arrastra para reordenar" style={{ opacity: 0.4, fontSize: 14, padding: 4, userSelect: 'none' }}>↕️</span>
          <button 
            onClick={() => toggleVisibility(proc.id)}
            title={proc.visible ? "Ocultar en canvas" : "Mostrar en canvas"}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
              opacity: proc.visible ? 1 : 0.3, padding: 4,
            }}
          >
            {proc.visible ? '👁️' : '👁️‍🗨️'}
          </button>
          <button 
            onClick={() => {
              if (window.confirm('¿Eliminar proceso?')) deleteProcess(proc.id);
            }}
            title="Eliminar proceso"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
              opacity: 0.3, padding: 4,
            }}
          >
            🗑️
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
      {lastValidationWarning && (
        <div
          style={{
            fontSize: 10,
            color: '#8a2d22',
            border: '1px solid #f0c9c2',
            background: '#fff3f1',
            borderRadius: 6,
            padding: '6px 8px',
            fontFamily: "'DM Mono',monospace",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span>⚠ {lastValidationWarning}</span>
          <button
            type="button"
            onClick={() => setLastValidationWarning(null)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#8a2d22',
              cursor: 'pointer',
              fontWeight: 700,
              padding: 0,
              lineHeight: 1,
            }}
            title="Cerrar advertencia"
          >
            ✕
          </button>
        </div>
      )}
      {/* Segments list */}
      {totalSegments > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {decoratedSegments.map(({ seg, idx, headerLabel, operatorBadge }) => {
            const fromLabel = nodes[seg.from]?.label || '?';
            const toLabel = nodes[seg.to]?.label || '?';
            const verbLabel = seg.label?.trim();
            const flowType = seg.flowType ?? 'normal';
            const isHovered = hoveredSegmentKey === `${proc.id}:${idx}`;
            return (
              <div
                key={idx}
                draggable
                onClick={e => {
                  e.stopPropagation();
                  if (draggingSegIdx === null) onSegmentEdit(proc.id, idx);
                }}
                onDragStart={e => {
                  e.dataTransfer.setData('text/plain', `${proc.id}:${idx}`);
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggingSegIdx(idx);
                }}
                onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverSegIdx !== idx) setDragOverSegIdx(idx);
                }}
                onDrop={e => {
                  e.preventDefault();
                  if (draggingSegIdx !== null && draggingSegIdx !== idx) {
                    reorderSegment(proc.id, draggingSegIdx, idx);
                  }
                  setDraggingSegIdx(null);
                  setDragOverSegIdx(null);
                }}
                onDragEnd={() => {
                  setDraggingSegIdx(null);
                  setDragOverSegIdx(null);
                }}
                style={{
                  position: 'relative',
                  fontSize: 11,
                  fontFamily: "'DM Mono',monospace",
                  color: '#666',
                  padding: '4px 8px',
                  paddingLeft: `${8 }px`,
                  background: isHovered ? `${proc.color}14` : '#f8f7f2',
                  borderRadius: 4,
                  cursor: draggingSegIdx === idx ? 'grabbing' : 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  border: `1px solid ${
                    dragOverSegIdx === idx && draggingSegIdx !== null && draggingSegIdx !== idx
                      ? proc.color + '66'
                      : isHovered
                        ? proc.color + '88'
                        : 'transparent'
                  }`,
                  transition: 'background 0.1s, border-color 0.1s',
                  opacity: draggingSegIdx === idx ? 0.65 : 1,
                }}
                onMouseEnter={e => {
                  onSegmentHover?.(proc.id, idx, true);
                  if (!(dragOverSegIdx === idx && draggingSegIdx !== null && draggingSegIdx !== idx)) {
                    e.currentTarget.style.borderColor = proc.color + '44';
                  }
                }}
                onMouseLeave={e => {
                  onSegmentHover?.(proc.id, idx, false);
                  if (!(dragOverSegIdx === idx && draggingSegIdx !== null && draggingSegIdx !== idx)) {
                    e.currentTarget.style.borderColor = isHovered ? proc.color + '88' : 'transparent';
                  }
                }}
              >
                
                <span style={{ opacity: 0.4 }}>{idx + 1}.</span>
                <span style={{
                  fontSize: 9,
                  borderRadius: 999,
                  padding: '1px 5px',
                  letterSpacing: '0.04em',
                  fontWeight: 700,
                  color: getOperatorColor(seg, 'vivid'),
                  background: `${getOperatorColor(seg, 'vivid')}1a`,
                }}>
                  {operatorBadge}
                </span>
                <span title={flowType} style={{ fontSize: 11, opacity: 0.7 }}>
                  {FLOW_EMOJI[flowType] ?? FLOW_EMOJI.normal}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {headerLabel ? `${headerLabel} · ` : ''}
                  {fromLabel}
                  {verbLabel ? ` — ${verbLabel} → ` : ' → '}
                  {toLabel}
                </span>
                {seg.friction !== 'none' && (
                  <span style={{ color: seg.friction === 'high' ? '#c0392b' : '#e67e22' }}>
                    {seg.friction === 'high' ? '⚡⚡' : '⚡'}
                  </span>
                )}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    if (window.confirm('¿Eliminar thread/paso?')) deleteSegment(proc.id, idx);
                  }}
                  title="Eliminar thread/paso"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#bbb',
                    cursor: 'pointer',
                    fontSize: 11,
                    padding: 0,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ 
          fontSize: 11, color: '#aaa', fontStyle: 'italic', textAlign: 'center', 
          padding: '8px 0', border: '1px dashed #eee', borderRadius: 6
        }}>
          Sin pasos registrados
        </div>
      )}

      {/* Footer / Actions */}
      {!isActive && (
        <button 
          onClick={() => setActive(proc.id)}
          style={{
            marginTop: 4,
            padding: '6px 12px',
            background: '#fff',
            border: `1px solid ${proc.color}44`,
            borderRadius: 6,
            color: proc.color,
            fontSize: 13,
            fontFamily: "'Caveat',cursive",
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = proc.color;
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.color = proc.color;
          }}
        >
          Seguir editando
        </button>
      )}
      
      {isActive && (
        <div style={{ 
          fontSize: 10, 
          color: proc.color, 
          fontWeight: 700, 
          fontFamily: "'DM Mono',monospace",
          textTransform: 'uppercase',
          letterSpacing: '1px',
          textAlign: 'center',
          marginTop: 4
        }}>
          ● Editando ahora
        </div>
      )}

      {copyFeedback && (
        <div style={{
          fontSize: 10,
          color: '#7a746a',
          fontFamily: "'DM Mono',monospace",
          textAlign: 'center',
          marginTop: 2,
        }}>
          {copyFeedback}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ProcessCard;
